class PromoBar extends HTMLElement {
  constructor() {
    super();
    this.itemsWrapper = this.querySelector('.swiper-wrapper');
    FoxTheme.Motion.inView(this, this.initializeCarousel.bind(this), { margin: '200px 0px 200px 0px' });
    
    this.setPromoBarHeight();
    this.checkTextOverflow();

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.setPromoBarHeight();
        this.checkTextOverflow();
      });
      this.resizeObserver.observe(this.promoBarSection);
    }

    this.boundScrollHandler = this.onScrollHandler.bind(this);
    this.initStickyBehavior();
    this.initPopupTriggers();
  }

  disconnectedCallback() {
    this.outlineObserver?.disconnect();
    this.outlineObserver = null;

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    window.removeEventListener('scroll', this.boundScrollHandler, false);

    if (this.carousel) {
      try {
        this.carousel.destroy(true, true);
      } catch (e) {}
      this.carousel = null;
    }
  }

  initPopupTriggers() {
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('.promo-bar__popup-trigger');
      if (!trigger) return;
      event.preventDefault();
      const campaignID = parseInt((trigger.dataset.aliaCampaign || '').replace(/\D/g, ''), 10);
      if (!campaignID) return;
      window.alia = window.alia || [];
      window.alia.push({ type: 'open', campaignID: campaignID, ignoreTargeting: true });
    });
  }

  get promoBarSection() {
    return this.closest('.promo-bar-section');
  }

  get isSticky() {
    return this.promoBarSection?.classList.contains('promo-bar-section--sticky');
  }

  initStickyBehavior() {
    if (!this.isSticky) return;
    
    this.currentScrollTop = 0;
    this.firstScrollTop = window.scrollY;
    this.promoBarBounds = this.promoBarSection.getBoundingClientRect();
    
    window.addEventListener('scroll', this.boundScrollHandler, false);
  }

  setPromoBarHeight() {
    if (!this.promoBarSection) return;
    
    requestAnimationFrame(() => {
      if (this.isSticky) {
        const height = Math.round(this.promoBarSection.offsetHeight);
        document.documentElement.style.setProperty('--promo-bar-height', `${height}px`);
      } else {
        document.documentElement.style.removeProperty('--promo-bar-height');
      }
    });
  }

  checkTextOverflow() {
    const textElement = this.querySelector('.promo-bar__text');
    const arrows = this.querySelectorAll('.promo-bar__nav-btn');
    const container = this.querySelector('.promo-bar');
    
    if (!textElement || !container) return;
    
    requestAnimationFrame(() => {
      // Reset to single line first to measure actual text width
      this.promoBarSection.classList.remove('promo-bar--multiline');
      
      const containerWidth = container.offsetWidth;
      
      // Calculate actual arrow space including their positioning
      let totalArrowSpace = 0;
      if (arrows.length > 0) {
        arrows.forEach(arrow => {
          totalArrowSpace += arrow.offsetWidth + 20; // arrow width + positioning padding
        });
      }
      
      const padding = 20; // Base padding
      const availableWidth = containerWidth - totalArrowSpace - padding;
      
      // Measure actual text width
      const textWidth = textElement.scrollWidth;
      
      if (textWidth > availableWidth && arrows.length > 0) {
        this.promoBarSection.classList.add('promo-bar--multiline');
      } else {
        this.promoBarSection.classList.remove('promo-bar--multiline');
      }
    });
  }

  onScrollHandler() {
    if (!this.isSticky) return;
    
    const scrollTop = window.scrollY;
    const promoBarSection = this.promoBarSection;
    const promoBarBoundsTop = this.promoBarBounds.top + this.firstScrollTop;
    
    requestAnimationFrame(() => {
      if (scrollTop > promoBarBoundsTop) {
        promoBarSection.classList.add('promo-bar-scrolled');
      } else {
        promoBarSection.classList.remove('promo-bar-scrolled');
      }
      
      this.currentScrollTop = scrollTop;
    });
  }

  getNextSlideButton() {
    return this.querySelector('.promo-bar__nav-btn--next') || this.querySelector('.swiper-button-next');
  }

  getPrevSlideButton() {
    return this.querySelector('.promo-bar__nav-btn--prev') || this.querySelector('.swiper-button-prev');
  }

  get getSlideItems() {
    return (this._cachedSlideItems = this._cachedSlideItems || Array.from(this.itemsWrapper?.children || []));
  }

  isAutoplayEnabled() {
    return this.getAttribute('data-autoplay') === 'true' || this.dataset.autoplay === 'true';
  }

  getAutoplaySpeed() {
    const raw = this.getAttribute('data-autoplay-speed') || this.dataset.autoplaySpeed || '5';
    const seconds = parseInt(raw, 10);
    return Number.isFinite(seconds) ? seconds * 1000 : 5000;
  }

  isNavigationEnabled() {
    return this.getAttribute('data-show-navigation') === 'true' || this.dataset.showNavigation === 'true';
  }

  updateSlideStates() {
    this.querySelectorAll('.swiper-slide').forEach((slide) => {
      const isActive = slide.classList.contains('swiper-slide-active');
      slide.toggleAttribute('inert', !isActive);
      slide.querySelectorAll('a, button, [tabindex]').forEach((el) => {
        if (isActive) {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', '-1');
        }
      });
    });
  }

  stopAutoplay() {
    try { this.carousel?.slider?.autoplay?.stop(); } catch {}
    this.itemsWrapper?.setAttribute('aria-live', 'polite');
  }

  startAutoplay() {
    if (this.userPaused || !this.isAutoplayEnabled()) return;
    try { this.carousel?.slider?.autoplay?.start(); } catch {}
    this.itemsWrapper?.setAttribute('aria-live', 'off');
  }

  /*
    section-promo-bar.css is loaded with media="print" and switched to "all" on
    load, so its rules are inert for the first frames. The pause control ships
    hidden because, unstyled, it would be in flow and push the bar down; unhide
    it only once that switch has happened. window.load is the backstop so a
    stylesheet that never arrives cannot leave the control unreachable.
  */
  revealPauseControl() {
    if (!this.pauseButton?.hidden) return;
    const reveal = () => (this.pauseButton.hidden = false);
    const link = document.querySelector('link[href*="section-promo-bar.css"]');

    if (!link || link.media === 'all') {
      reveal();
    } else {
      link.addEventListener('load', reveal, { once: true });
      window.addEventListener('load', reveal, { once: true });
    }
  }

  setupAccessibilityControls() {
    this.userPaused = false;
    this.hasFocusWithin = false;
    this.itemsWrapper?.setAttribute('aria-live', this.isAutoplayEnabled() ? 'off' : 'polite');

    // The always-visible control renders outside .promo-bar-section, so that the
    // ResizeObserver watching that element never sees it; look one level up.
    this.pauseButton =
      this.closest('.shopify-section')?.querySelector('.promo-bar__pause-control') ||
      this.querySelector('.promo-bar__pause-btn');
    if (this.pauseButton) {
      this.pauseButton.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        if (this.userPaused) {
          this.stopAutoplay();
        } else {
          this.startAutoplay();
        }
        const label = this.userPaused
          ? this.pauseButton.dataset.labelPlay
          : this.pauseButton.dataset.labelPause;
        this.pauseButton.setAttribute('aria-label', label);
        // The always-visible control swaps icons from CSS off this attribute;
        // the legacy focus-only button is a text label instead.
        this.pauseButton.toggleAttribute('data-paused', this.userPaused);
        if (this.pauseButton.classList.contains('promo-bar__pause-btn')) {
          this.pauseButton.textContent = label;
        }
      });
      this.revealPauseControl();
    }

    // UserWay's remediation script forces a blue inline outline on focused
    // elements; inline !important can't be beaten from CSS, so recolor it here.
    this.outlineObserver?.disconnect();
    this.outlineObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const el = mutation.target;
        if (el.nodeType === 1 && el.style.outlineColor === 'rgb(0, 24, 255)') {
          el.style.setProperty('outline-color', '#ffffff', 'important');
        }
      });
    });
    this.outlineObserver.observe(this, { attributes: true, attributeFilter: ['style'], subtree: true });

    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => {
      if (!this.hasFocusWithin) this.startAutoplay();
    });
    this.addEventListener('focusin', () => {
      this.hasFocusWithin = true;
      this.stopAutoplay();
      this.scrollLeft = 0;
    });
    this.addEventListener('focusout', (event) => {
      if (this.contains(event.relatedTarget)) return;
      this.hasFocusWithin = false;
      this.startAutoplay();
    });
  }

  initializeCarousel() {
    if (this.carousel || this._initializing) return;
    this._initializing = true;
  
    const slides = this.getSlideItems;
    if (slides.length <= 1) {
      const nav = this.querySelector('.promo-bar__navigation');
      if (nav) nav.style.display = 'none';
      this._initializing = false;
      return;
    }
  
    const autoplay = this.isAutoplayEnabled()
      ? {
          delay: this.getAutoplaySpeed(),
          pauseOnMouseEnter: false,
          disableOnInteraction: false,
        }
      : false;
  
    const navigation = this.isNavigationEnabled()
      ? {
          nextEl: this.getNextSlideButton(),
          prevEl: this.getPrevSlideButton(),
        }
      : undefined;
  
    const options = {
      loop: true,
      autoplay,
      navigation,
      on: {
        slideChange: () => {
          this.updateSlideStates();
          const s = this.carousel?.slider;
          if (!this.userPaused && !this.hasFocusWithin && this.isAutoplayEnabled() && s?.params?.autoplay && !s.autoplay?.running) {
            try { s.autoplay.start(); } catch {}
          }
        },
        slideChangeTransitionEnd: () => {
          this.updateSlideStates();
        },
      },
    };
  
    try {
      this.carousel = new FoxTheme.Carousel(this, options, FoxTheme.Swiper.Autoplay);
      this.carousel.init();
    } finally {
      this._initializing = false;
    }

    this.updateSlideStates();
    requestAnimationFrame(() => this.updateSlideStates());
    this.setupAccessibilityControls();
    this.startAutoplay();

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => {
        this.carousel?.slider?.slideToLoop(slides.indexOf(event.target));
      });
    }
  }
}

if (!customElements.get('promo-bar')) {
  customElements.define('promo-bar', PromoBar);
}

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function (event) {
    const promoBar = event.target.querySelector('promo-bar');
    if (promoBar) {
      setTimeout(() => {
        promoBar.initializeCarousel();
      }, 200);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const promoBar = event.target.querySelector('promo-bar');
    if (promoBar) promoBar.disconnectedCallback();
  });
}